import { inject, Injectable } from '@angular/core';

import { IGetParentSettingRequest, IGetParentSettingResponse,
    ISaveGlobalSettingRequest, ISaveParentSettingRequest, ISettingItem,
    OnsiteCheckInClient
} from '@azavista/planner-lib';
import { IAdvancedFilter, IAdvancedSearchData, ITableSettingsChangedData } from '@azavista/components/shared';
import { SettingEntityType, SettingParentType, SettingType } from './enums';
import { IColumnsListSetting, IFieldIdsSettingItem } from './interfaces';
import { SharedService } from './shared.service';
import { IModifiedTableColumnsData } from '@azavista/components/table';
import { SearchParams } from '@azavista/advanced-search';
import { OnsiteCheckInClientService } from './components/onsite-checkin-client/onsite-checkin-client.service';

@Injectable({
    providedIn: 'root'
})
export class SettingsService {
    client = inject(OnsiteCheckInClient);
    clientSvc = inject(OnsiteCheckInClientService);

    constructor(
        private readonly sharedSvc: SharedService
    ) {
    }

    async saveParentAdvancedSearchFilters(
        tableData: ITableSettingsChangedData, advSearchData: IAdvancedSearchData,
        parentType: SettingParentType, parentEntityId: string, entityType: SettingEntityType
    ): Promise<void> {
        let advancedFilters = this.getAdvancedFilters(tableData, advSearchData);
        if (!advancedFilters) {
            return;
        }
        advancedFilters = advancedFilters.filter(x => !x.isTemporary);
        const searchParamsFilters = this.createSearchParamsFilters(advancedFilters);
        const setting: IAdvancedSearchSetting = { advancedFilters: advancedFilters, searchParamsFilters: searchParamsFilters };
        const req: ISaveParentSettingRequest = {
            parentType: parentType, parentEntityId: parentEntityId, settingType: SettingType.advancedSearch,
            entityType: entityType, setting: setting
        };
        await this.saveParentSetting(req);
    }

    getAdvancedFilters(tableData: ITableSettingsChangedData, advSearchData: IAdvancedSearchData): IAdvancedFilter[] | null {
        const advSearch = tableData.advancedSearch;
        if (!advSearch) {
            // No changes in advanced search filters
            return null;
        }
        const hasChanges = advSearch.createdFilter || advSearch.deletedFilter || advSearch.savedFilter;
        if (!hasChanges) {
            // No changes in advanced search filters
            return null;
        }
        let advancedFilters: IAdvancedFilter[] = advSearchData.advancedFilters;
        if (advSearch.deletedFilter) {
            advancedFilters = advancedFilters.filter(x => x.id !== advSearch.deletedFilter.id);
        }
        // Remove filters with no criteria
        advancedFilters = advancedFilters.filter(x => x.criteria && x.criteria.length > 0);
        return advancedFilters;
    }

    createSearchParamsFilters(advancedFilters: IAdvancedFilter[]): ISearchParamsFilter[] {
        const filters: ISearchParamsFilter[] = [];
        for (const filter of advancedFilters) {
            const searchParam = this.sharedSvc.createSearchParams(filter.criteria);
            filters.push({ id: filter.id, name: filter.name, searchParams: searchParam });
        }
        return filters;
    }

    async saveParentColumnsSettings(
        parentType: SettingParentType, parentEntityId: string, entityType: SettingEntityType,
        data: IModifiedTableColumnsData
    ): Promise<void> {
        const setting: IColumnsListSetting = { columnIdsOrder: data.selectedIds, columnsIdsWithWidths: data.columnsWidths };
        const req: ISaveParentSettingRequest = {
            parentType: parentType, parentEntityId: parentEntityId, entityType: entityType, settingType: SettingType.listingColumns,
            setting: setting
        };
        await this.saveParentSetting(req);
    }

    async saveParentSetting(req: ISaveParentSettingRequest) {
        await this.clientSvc.parentSettingCollection.mutations.save(req);
    }

    async getParentAdvancedSearchFilters(
        parentType: SettingParentType, parentEntityId: string, entityType: SettingEntityType
    ): Promise<IAdvancedFilter[]> {
        let advancedFilters: IAdvancedFilter[] = [];
        try {
            const req: IGetParentSettingRequest = {
                parentType: parentType, parentEntityId: parentEntityId, entityType: entityType,
                settingType: SettingType.advancedSearch
            };
            const res = await this.getParentSetting(req);
            advancedFilters = res.setting.advancedFilters;
        } catch (err) {

        }
        return advancedFilters;
    }

    async getParentColumnsOrder(
        parentType: SettingParentType, parentEntityId: string,
        entityType: SettingEntityType, allowedIds: string[], defaultIds: string[]
    ): Promise<IColumnsListSetting> {
        let columnIds: string[] = [];
        let res: IRegressionGetSettingResponse;
        try {
            const req: IGetParentSettingRequest = {
                parentType: parentType, parentEntityId: parentEntityId,
                entityType: entityType, settingType: SettingType.listingColumns
            };
            res = await this.getParentSetting(req);
            const setting = res.setting as IColumnsListSetting;
            // We must filter ids that are not part of the table columns
            columnIds = this.sharedSvc.filterNotAllowed(setting.columnIdsOrder, allowedIds);
        } catch (err) {
            // Returns HTTP 404 in case the setting is not found
        }
        if (!columnIds || !columnIds.length) {
            columnIds = defaultIds;
        }
        const result = {} as IColumnsListSetting;
        result.columnIdsOrder = columnIds;
        result.columnsIdsWithWidths = (res && res.setting) ? (res.setting as IColumnsListSetting).columnsIdsWithWidths || [] : [];
        return result;
    }

    async getSelectedParentColumns(
        entityType: string, parentType: string, parentEntityId: string, settingType: string, settingName: string
    ): Promise<string[] | null> {
        let result: string[] | null = null;
        try {
            const req: IGetParentSettingRequest = {
                entityType: entityType, parentEntityId: parentEntityId, parentType: parentType,
                settingType: settingType
            };
            const res = await this.getParentSettings(req);
            result = this.getFieldIdsFromSettings(res.settings, settingName);
        } catch {
            result = null;
        }
        return result;
    }

    async saveSelectedParentColumns(
        fieldIds: string[], entityType: string, parentType: string, parentEntityId: string, settingType: string,
        settingItemName: string
    ): Promise<void> {
        const setting: IFieldIdsSettingItem = {
            fieldIds: fieldIds
        };
        const settingItem: ISettingItem = {
            name: settingItemName,
            setting: setting
        };
        const req: ISaveParentSettingRequest = {
            entityType: entityType, parentType: parentType, parentEntityId: parentEntityId,
            settingType: settingType,
            setting: settingItem
        };
        await this.saveParentSetting(req);
    }

    async getSelectedGlobalColumns(entityType: string, settingType: string, settingName: string): Promise<string[] | null> {
        let result: string[] | null = null;
        try {
            const res = (await this.clientSvc.globalSettingCollection.getOne('default', `${entityType}___${settingType}`)).data;
            result = this.getFieldIdsFromSettings(res.settings, settingName);
        } catch {
            result = null;
        }
        return result;
    }

    async saveSelectedGlobalColumns(fieldIds: string[], entityType: string, settingType: string, settingName: string): Promise<void> {
        const setting: IFieldIdsSettingItem = {
            fieldIds: fieldIds
        };
        const settingItem: ISettingItem = {
            name: settingName,
            setting: setting
        };
        const saveSettingsReq: ISaveGlobalSettingRequest = {
            entityType: entityType, settingType: settingType,
            setting: settingItem
        };
        try {
            await this.clientSvc.globalSettingCollection.mutations.save(saveSettingsReq);
        } catch {
            //
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getParentSetting<SettingsType = any>(req: IGetParentSettingRequest): Promise<IRegressionGetSettingResponse<SettingsType>> {
        const res: IGetParentSettingResponse = await this.getParentSettings(req);
        return res?.settings[0];
    }

    async getParentSettings({entityType, settingType, parentEntityId, parentType}: IGetParentSettingRequest): Promise<IGetParentSettingResponse> {
        return (await this.clientSvc.parentSettingCollection.getOne('default', `${entityType}___${settingType}___${parentType}___${parentEntityId}`, {
            strategy: 'prefer-network',
        })).data;
    }

    private getFieldIdsFromSettings(settingItems: ISettingItem[], settingName: string): string[] | null {
        if (settingItems) {
            const settingItem = settingItems.find(x => x.setting?.name === settingName);
            if (settingItem?.setting?.setting) {
                return (settingItem?.setting?.setting as IFieldIdsSettingItem).fieldIds;
            }
        }
        return null;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface IRegressionGetSettingResponse<T = any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setting: T;
}

interface IAdvancedSearchSetting {
    advancedFilters: IAdvancedFilter[];
    searchParamsFilters: ISearchParamsFilter[];
}

interface ISearchParamsFilter {
    id: string;
    name: string;
    searchParams: SearchParams;
}
